import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import {
  AlertTriangle, ArrowDownLeft, ArrowUpRight, BarChart3, Bell, CalendarCheck, CheckCircle2, ChevronDown,
  Clock, Coins, Gamepad2, IdCard, Info, MessageSquare, PlayCircle, QrCode, Receipt, Table2, Users, UserPlus,
  Wallet, Armchair,
} from 'lucide-react'
import QRCodeModal from './QRCodeModal'
import '../assests/glass-theme.css'
import {
  getDashboardStats,
  getAdminDashboardStats,
  getPokerTutorials,
  getLiveFloorStatus,
  getActiveSessions,
  getDealerStatus,
  getDashboardAlerts,
  getRevenueChartData,
  getPlayerGrowthData,
  getClubRevenueData,
  getRecentTransactions,
  getPendingApprovals,
  getTodaysHighlights
} from '../actions/dashboardActions'
import {
  Button, Card, CardHeader, ChartCard, DonutChart, BarsChart, TrendChart, EmptyState, PageHeader, PageLoader,
  StatCard, StatGrid, StatusPill, Skeleton, IconChip, formatINR, formatNumber, useChartColors, cx,
} from '../ui'
import { PlayerPage } from '../components/player/PlayerChrome'

const MONEY_IN = ['deposit', 'chip-deposit', 'cashout']

const Dashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [userDetails, setUserDetails] = useState(null)
  const [showQRModal, setShowQRModal] = useState(false)

  const adminLogin = useSelector((state) => state.adminLogin)
  const { getRoleData } = useSelector((state) => state.getRoleInfo)
  const isAdmin = Boolean(adminLogin?.userInfo?.token)

  useEffect(() => {
    // Read auth from localStorage (stable, no re-render dependency)
    const storedAdminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
    const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    const hasAdminSession = Boolean(storedAdminInfo?.token)
    const resolvedToken = hasAdminSession ? storedAdminInfo?.token : storedUserInfo?.token
    const resolvedUserId = hasAdminSession
      ? (storedAdminInfo?.id || storedAdminInfo?._id)
      : (storedUserInfo?.id || storedUserInfo?._id)

    if (!resolvedToken || !resolvedUserId) {
      navigate('/login')
      return
    }

    setUserDetails(hasAdminSession ? storedAdminInfo : storedUserInfo)

    dispatch(getDashboardStats())
    dispatch(getPokerTutorials())

    if (hasAdminSession) {
      dispatch(getAdminDashboardStats())
      dispatch(getLiveFloorStatus())
      dispatch(getActiveSessions())
      dispatch(getDealerStatus())
      dispatch(getDashboardAlerts())
      dispatch(getRevenueChartData())
      dispatch(getPlayerGrowthData())
      dispatch(getClubRevenueData())
      dispatch(getRecentTransactions())
      dispatch(getPendingApprovals())
      dispatch(getTodaysHighlights())
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const storedAdminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
    if (getRoleData && !storedAdminInfo?.token) {
      if (!getRoleData.dashboard) {
        toast('Not Authorized', { type: 'error', hideProgressBar: true, autoClose: 2000 })
        navigate('/')
      }
    }
  }, [getRoleData, navigate])

  // Bottom-nav QR button on other player pages links here with ?qr=1
  useEffect(() => {
    if (new URLSearchParams(location.search).get('qr') === '1') setShowQRModal(true)
  }, [location.search])

  if (!userDetails) return <PageLoader fullScreen={!isAdmin} label="Loading your dashboard…" />

  return (
    <>
      {isAdmin ? <AdminDashboard userDetails={userDetails} /> : <PlayerHome userDetails={userDetails} onQr={() => setShowQRModal(true)} />}
      <QRCodeModal isOpen={showQRModal} onClose={() => setShowQRModal(false)} />
    </>
  )
}

/* ────────────────────────────────────────────────────────────────
   Staff dashboard
   ──────────────────────────────────────────────────────────────── */

const greeting = () => {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

const AdminDashboard = ({ userDetails }) => {
  const navigate = useNavigate()
  const colors = useChartColors()

  const { loading: adminStatsLoading, stats: adminStats } = useSelector((state) => state.dashboardAdminStats)
  const { loading: liveFloorLoading, data: liveFloorData } = useSelector((state) => state.dashboardLiveFloor)
  const { loading: sessionsLoading, data: sessionsData } = useSelector((state) => state.dashboardActiveSessions)
  const { loading: dealerLoading, data: dealerData } = useSelector((state) => state.dashboardDealerStatus)
  const { loading: alertsLoading, alerts } = useSelector((state) => state.dashboardAlerts)
  const { loading: revenueChartLoading, data: revenueChartData } = useSelector((state) => state.dashboardRevenueChart)
  const { loading: playerGrowthLoading, data: playerGrowthData } = useSelector((state) => state.dashboardPlayerGrowth)
  const { loading: clubRevenueLoading, data: clubRevenueData } = useSelector((state) => state.dashboardClubRevenue)
  const { loading: recentTxnLoading, transactions: recentTransactions } = useSelector((state) => state.dashboardRecentTxn)
  const { data: pendingData } = useSelector((state) => state.dashboardPendingApprovals)
  const { loading: highlightsLoading, data: highlightsData } = useSelector((state) => state.dashboardHighlights)

  const h = highlightsData || {}
  const statsLoading = adminStatsLoading && !adminStats
  const activeNow = adminStats?.players?.activeNow ?? adminStats?.players?.active ?? 0

  const pendingItems = [
    { key: 'kyc', label: 'KYC checks', icon: IdCard, to: '/manual-kyc' },
    { key: 'commissions', label: 'Rake to approve', icon: Coins, to: '/daily-commission' },
    { key: 'transactions', label: 'Transactions', icon: Receipt, to: '/admin/transactions' },
    { key: 'bookings', label: 'Seat bookings', icon: CalendarCheck },
  ].filter((p) => (pendingData?.[p.key] || 0) > 0)

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow={new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
        title={`${greeting()}, ${(userDetails.name || 'there').split(' ')[0]}`}
        description="Today across your clubs: money in, players on the floor, and anything waiting on you."
        actions={
          <>
            <Button variant="secondary" icon={BarChart3} onClick={() => navigate('/reports')}>Reports</Button>
            <Button icon={Wallet} onClick={() => navigate('/admin/transactions')}>Transactions</Button>
          </>
        }
      />

      {/* 1 · The numbers that matter today */}
      <StatGrid>
        <StatCard
          label="Top-ups today"
          icon={ArrowDownLeft}
          value={formatINR(adminStats?.today?.deposits ?? h.deposits?.value)}
          change={h.deposits?.change}
          changeLabel="vs yesterday"
          loading={statsLoading}
        />
        <StatCard
          label="Players at tables"
          icon={Users}
          value={formatNumber(activeNow)}
          hint={`${formatNumber(adminStats?.players?.total || 0)} registered · +${adminStats?.players?.newToday || 0} today`}
          loading={statsLoading}
          onClick={() => navigate('/players')}
        />
        <StatCard
          label="Waiting on you"
          icon={Clock}
          tone={pendingData?.total ? 'warning' : undefined}
          value={formatNumber(pendingData?.total || 0)}
          hint={pendingData?.total ? 'approvals pending' : 'All caught up'}
          loading={statsLoading}
        />
        <StatCard
          label="Buy-in volume"
          icon={Gamepad2}
          value={formatINR(adminStats?.today?.buyIn ?? h.buyIn?.value)}
          change={h.buyIn?.change}
          changeLabel="vs yesterday"
          loading={statsLoading}
        />
        <StatCard
          label="Transactions today"
          icon={Receipt}
          value={formatNumber(adminStats?.today?.transactions ?? h.transactions?.value ?? 0)}
          change={h.transactions?.change}
          changeLabel="vs yesterday"
          loading={statsLoading}
        />
        <StatCard
          label="New players"
          icon={UserPlus}
          value={formatNumber(h.newPlayers?.value ?? adminStats?.players?.newToday ?? 0)}
          change={h.newPlayers?.change}
          changeLabel="vs yesterday"
          loading={highlightsLoading && !highlightsData}
        />
        <StatCard
          label="Clubs open"
          icon={Table2}
          value={formatNumber(adminStats?.clubs?.active || 0)}
          hint={liveFloorData?.summary ? `${liveFloorData.summary.active || 0} tables running` : undefined}
          loading={statsLoading}
          onClick={() => navigate('/venues')}
        />
      </StatGrid>

      {/* 2 · Needs attention */}
      {(pendingItems.length > 0 || (alerts && alerts.length > 0) || alertsLoading) && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          {pendingItems.length > 0 && (
            <Card className="xl:col-span-2">
              <CardHeader title="Waiting for approval" description="Clear these to keep the floor moving." />
              <div className="grid grid-cols-2 gap-3">
                {pendingItems.map((p) => {
                  const Icon = p.icon
                  const Tag = p.to ? 'button' : 'div'
                  return (
                    <Tag
                      key={p.key}
                      type={p.to ? 'button' : undefined}
                      onClick={p.to ? () => navigate(p.to) : undefined}
                      className={cx(
                        'flex items-center gap-3 p-3 rounded-cm-lg bg-cm-warning-soft text-left',
                        p.to && 'hover:ring-1 hover:ring-[var(--cm-warning)] transition-shadow',
                      )}
                    >
                      <Icon size={18} className="text-cm-warning shrink-0" />
                      <span className="min-w-0">
                        <span className="block font-display text-xl font-extrabold text-cm-text tabular-nums">{pendingData[p.key]}</span>
                        <span className="block text-xs text-cm-muted">{p.label}</span>
                      </span>
                    </Tag>
                  )
                })}
              </div>
            </Card>
          )}
          <Card className={pendingItems.length > 0 ? 'xl:col-span-3' : 'xl:col-span-5'}>
            <CardHeader title="Recent alerts" description="Things that happened on the floor that you may want to check." />
            {alertsLoading && !alerts ? (
              <div className="flex flex-col gap-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : alerts && alerts.length > 0 ? (
              <ul className="flex flex-col gap-2 m-0 p-0 list-none max-h-72 overflow-y-auto">
                {alerts.slice(0, 6).map((alert, index) => {
                  const tone = alert.type === 'warning' ? 'warning' : alert.type === 'success' ? 'success' : 'info'
                  const Icon = tone === 'warning' ? AlertTriangle : tone === 'success' ? CheckCircle2 : Info
                  return (
                    <li key={index} className="flex items-start gap-3 p-3 rounded-cm bg-cm-surface-2">
                      <IconChip icon={Icon} tone={tone} size={32} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-cm-text m-0">{alert.message}</p>
                        <p className="text-xs text-cm-muted m-0 mt-0.5">{alert.timeAgo}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <EmptyState compact icon={Bell} title="No alerts" description="Nothing unusual on the floor right now." />
            )}
          </Card>
        </div>
      )}

      {/* 3 · Trends */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard
          className="xl:col-span-2"
          title="Is revenue growing this week?"
          description="Top-ups per day · last 7 days"
          legend={[{ label: 'Top-ups', color: colors.chart[0] }]}
          loading={revenueChartLoading && !revenueChartData}
          empty={!revenueChartData || revenueChartData.length === 0}
          height={280}
        >
          <BarsChart
            data={revenueChartData || []}
            xKey="day"
            money
            series={[{ key: 'deposits', name: 'Top-ups' }]}
          />
        </ChartCard>
        <ChartCard
          title="Where rake comes from"
          description="Share by club · this month"
          loading={clubRevenueLoading && !clubRevenueData}
          empty={!clubRevenueData?.clubs?.length}
          height="auto"
        >
          <DonutChart
            money
            centerLabel="total rake"
            height={180}
            data={(clubRevenueData?.clubs || []).map((c) => ({ name: c.clubName, value: Number(c.totalRake) || 0 }))}
          />
        </ChartCard>
      </div>

      <ChartCard
        title="How busy are the clubs?"
        description="Active and new players per day · last 7 days"
        legend={[{ label: 'Active players', color: colors.chart[0] }, { label: 'New players', color: colors.chart[1] }]}
        loading={playerGrowthLoading && !playerGrowthData}
        empty={!playerGrowthData?.days?.length}
        height={240}
      >
        <TrendChart
          data={playerGrowthData?.days || []}
          xKey="day"
          series={[{ key: 'activePlayers', name: 'Active players' }, { key: 'newPlayers', name: 'New players', fill: false }]}
          showDots
        />
      </ChartCard>

      {/* 4 · Live floor */}
      <Card>
        <CardHeader
          title="Live floor"
          description="Every table and how full it is right now."
          actions={
            liveFloorData?.summary && (
              <>
                <StatusPill tone="success" icon={Table2}>{liveFloorData.summary.active || 0} running</StatusPill>
                <StatusPill tone="neutral" icon={Armchair}>
                  {liveFloorData.summary.occupiedSeats || 0}/{liveFloorData.summary.totalSeats || 0} seats taken
                </StatusPill>
              </>
            )
          }
        />
        {liveFloorLoading && !liveFloorData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
        ) : liveFloorData?.tables?.length ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3">
              {liveFloorData.tables.slice(0, 12).map((table) => {
                const pct = table.totalSeats ? Math.round((table.occupiedSeats / table.totalSeats) * 100) : 0
                const status = table.status === 'Active' ? 'success' : table.status === 'Reserved' ? 'warning' : 'neutral'
                return (
                  <div key={table.id} className="p-3 rounded-cm-lg border border-cm-border bg-cm-surface-2 flex flex-col gap-2 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display font-extrabold text-cm-text truncate">{table.tableNumber}</span>
                      <StatusPill size="sm" tone={status}>{table.status}</StatusPill>
                    </div>
                    <div className="text-xs text-cm-muted truncate">
                      {table.gameType || 'Hold’em'}{table.club ? ` · ${table.club}` : ''}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-cm-surface-3 overflow-hidden">
                        <div className="h-full rounded-full bg-cm-gold" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold tabular-nums text-cm-text">
                        {table.occupiedSeats}/{table.totalSeats}
                      </span>
                    </div>
                    {table.dealer && <div className="text-xs text-cm-muted truncate">Dealer: {table.dealer}</div>}
                  </div>
                )
              })}
            </div>
            {liveFloorData.tables.length > 12 && (
              <div className="flex justify-center mt-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dealer-management')}>
                  See all {liveFloorData.tables.length} tables
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState compact icon={Table2} title="No tables running" description="Tables will appear here as soon as a session starts." />
        )}
      </Card>

      {/* 5 · Detail lists */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card>
          <CardHeader
            title="Latest transactions"
            actions={<Button variant="ghost" size="sm" onClick={() => navigate('/admin/transactions')}>View all</Button>}
          />
          <ListOrState loading={recentTxnLoading && !recentTransactions} empty={!recentTransactions?.length} emptyTitle="No transactions yet" emptyIcon={Receipt}>
            {(recentTransactions || []).slice(0, 6).map((txn) => {
              const isIn = MONEY_IN.includes(txn.type)
              return (
                <Row
                  key={txn.id}
                  icon={isIn ? ArrowDownLeft : ArrowUpRight}
                  tone={isIn ? 'success' : 'gold'}
                  title={txn.player}
                  sub={`${String(txn.type || '').replace('-', ' ')} · ${txn.club || ''}`}
                  value={<span className={isIn ? 'text-cm-success' : 'text-cm-text'}>{isIn ? '+' : '−'}{formatINR(txn.amount)}</span>}
                  meta={txn.timeAgo}
                />
              )
            })}
          </ListOrState>
        </Card>

        <Card>
          <CardHeader
            title="Players in session"
            actions={<StatusPill tone="info" icon={Users}>{sessionsData?.totalActive || 0} live</StatusPill>}
          />
          <ListOrState loading={sessionsLoading && !sessionsData} empty={!sessionsData?.sessions?.length} emptyTitle="No one is playing yet" emptyIcon={Users}>
            {(sessionsData?.sessions || []).slice(0, 6).map((s) => (
              <Row
                key={s.id}
                avatar={s.player}
                title={s.player}
                sub={`${s.table} · ${s.duration}`}
                value={
                  <span className={s.profitLoss >= 0 ? 'text-cm-success' : 'text-cm-danger'}>
                    {formatINR(s.profitLoss, { sign: true })}
                  </span>
                }
                meta={`${formatINR(s.currentStack)} stack`}
              />
            ))}
          </ListOrState>
        </Card>

        <Card>
          <CardHeader
            title="Dealers on duty"
            actions={<StatusPill tone="gold" icon={Table2}>{dealerData?.totalOnDuty || 0} on duty</StatusPill>}
          />
          <ListOrState loading={dealerLoading && !dealerData} empty={!dealerData?.dealers?.length} emptyTitle="No dealers on duty" emptyIcon={Table2}>
            {(dealerData?.dealers || []).slice(0, 6).map((d) => (
              <Row
                key={d.id}
                avatar={d.dealer}
                title={d.dealer}
                sub={`Table ${d.table} · ${d.club}`}
                value={<span className="text-cm-text">{d.duration}</span>}
                meta={`${d.actionCount} actions`}
              />
            ))}
          </ListOrState>
        </Card>
      </div>

      {/* Quick links */}
      <Card>
        <CardHeader title="Jump to" />
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { label: 'Transactions', icon: Wallet, to: '/admin/transactions' },
            { label: 'Reports', icon: BarChart3, to: '/reports' },
            { label: 'Players', icon: Users, to: '/players' },
            { label: 'Venues', icon: Table2, to: '/venues' },
            { label: 'Messages', icon: MessageSquare, to: '/messaging' },
          ].map((q) => {
            const Icon = q.icon
            return (
              <button
                key={q.to}
                type="button"
                onClick={() => navigate(q.to)}
                className="flex flex-col items-center gap-2 p-3 rounded-cm-lg bg-cm-surface-2 border border-transparent hover:border-cm-border-strong transition-colors"
              >
                <Icon size={20} className="text-cm-gold-text" />
                <span className="text-xs font-semibold text-cm-text">{q.label}</span>
              </button>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

const ListOrState = ({ loading, empty, emptyTitle, emptyIcon, children }) => {
  if (loading) return <div className="flex flex-col gap-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-11" />)}</div>
  if (empty) return <EmptyState compact icon={emptyIcon} title={emptyTitle} />
  return <ul className="flex flex-col m-0 p-0 list-none divide-y divide-[var(--cm-border)]">{children}</ul>
}

const Row = ({ icon: Icon, tone = 'gold', avatar, title, sub, value, meta }) => (
  <li className="flex items-center gap-3 py-2.5">
    {Icon ? (
      <IconChip icon={Icon} tone={tone} />
    ) : (
      <span className="w-9 h-9 rounded-full grid place-items-center shrink-0 bg-cm-gold-soft text-cm-gold-text text-xs font-bold">
        {String(avatar || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
      </span>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-cm-text truncate m-0">{title}</p>
      <p className="text-xs text-cm-muted truncate m-0 capitalize">{sub}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="text-sm font-semibold tabular-nums m-0">{value}</p>
      {meta && <p className="text-xs text-cm-muted m-0">{meta}</p>}
    </div>
  </li>
)

/* ────────────────────────────────────────────────────────────────
   Player home (React player app)
   ──────────────────────────────────────────────────────────────── */

const txnLabel = (type) => String(type || 'transaction').replace('-', ' ')

const PlayerHome = ({ userDetails, onQr }) => {
  const navigate = useNavigate()
  const { loading: statsLoading, stats } = useSelector((state) => state.dashboardStats)
  const { tutorials } = useSelector((state) => state.dashboardTutorials)
  const [expandedTutorial, setExpandedTutorial] = useState(null)

  const playerName = userDetails?.name || 'Player'
  const playerId = userDetails?.identifier || ''
  const walletBal = stats?.walletBalance ?? 0
  const isVerified = userDetails?.verification?.isFullyVerified || false
  const streak = stats?.scoreStreak?.current || 0

  return (
      <PlayerPage
        title={`Hi, ${playerName.split(' ')[0]}`}
        subtitle={playerId ? `Player ID ${playerId}` : undefined}
        onQr={onQr}
        right={
          <button
            type="button"
            onClick={onQr}
            className="w-9 h-9 grid place-items-center rounded-cm bg-cm-gold text-cm-gold-ink"
            aria-label="Show my check-in QR code"
          >
            <QrCode size={18} />
          </button>
        }
      >
        {/* Balance first: it's what players open the app for */}
        <section className="balance-card relative overflow-hidden">
          <span aria-hidden="true" className="absolute -right-5 -top-5 w-24 h-24 rounded-full border-[10px] border-dashed border-cm-red opacity-25" />
          <div className="balance-label">Wallet balance</div>
          <div className="balance-amount mt-1.5">{statsLoading && !stats ? '…' : formatINR(walletBal)}</div>
          <div className="mt-3">
            {isVerified ? <StatusPill status="Verified">KYC verified</StatusPill> : <StatusPill tone="warning">KYC pending</StatusPill>}
          </div>
        </section>

        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: 'Book seat', icon: Armchair, onClick: () => navigate('/book-seat'), primary: true },
            { label: 'Wallet', icon: Wallet, onClick: () => navigate('/wallet') },
            { label: 'Bookings', icon: CalendarCheck, onClick: () => navigate('/my-bookings') },
          ].map((a) => {
            const Icon = a.icon
            return (
              <button
                key={a.label}
                type="button"
                onClick={a.onClick}
                className={cx(
                  'flex flex-col items-center gap-1.5 py-3 rounded-cm-lg border text-xs font-semibold',
                  a.primary ? 'bg-cm-gold text-cm-gold-ink border-transparent' : 'bg-cm-surface text-cm-text border-cm-border',
                )}
              >
                <Icon size={20} className={a.primary ? '' : 'text-cm-gold-text'} />
                {a.label}
              </button>
            )
          })}
        </div>

        {/* Streak */}
        <section className="mt-6">
          <h2 className="font-sans text-base font-bold mb-2.5">Score streak</h2>
          <div className="glass-dark p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-display text-3xl font-extrabold tabular-nums">{streak}<span className="text-base text-cm-muted font-bold"> / 7 days</span></div>
                <p className="text-xs text-cm-muted m-0 mt-1">
                  {streak >= 7 ? 'Weekly milestone reached — nice!' : `Play ${7 - streak} more day${7 - streak === 1 ? '' : 's'} for this week's bonus`}
                </p>
              </div>
              <div className="flex gap-1.5 text-xl" aria-hidden="true">
                {['♠', '♥', '♣', '♦'].map((suit, i) => (
                  <span key={suit} className={i < streak ? (i % 2 ? 'text-cm-red' : 'text-cm-gold') : 'text-cm-faint'}>{suit}</span>
                ))}
              </div>
            </div>
            <div className="h-2 rounded-full bg-cm-surface-3 overflow-hidden mt-3">
              <div className="h-full rounded-full bg-cm-gold transition-[width] duration-500" style={{ width: `${Math.min((streak / 7) * 100, 100)}%` }} />
            </div>
          </div>
        </section>

        {/* Recent activity */}
        <section className="mt-6">
          <h2 className="font-sans text-base font-bold mb-2.5">Recent activity</h2>
          <div className="glass-dark px-4">
            {statsLoading && !stats ? (
              <div className="py-4 flex flex-col gap-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : stats?.recentTransactions?.length > 0 ? (
              <ul className="m-0 p-0 list-none divide-y divide-[var(--cm-border)]">
                {stats.recentTransactions.map((txn, i) => {
                  const isIn = MONEY_IN.includes(txn.type)
                  return (
                    <li key={i} className="flex items-center gap-3 py-3">
                      <span className={cx('w-9 h-9 rounded-cm grid place-items-center shrink-0', isIn ? 'bg-cm-success-soft text-cm-success' : 'bg-cm-gold-soft text-cm-gold-text')}>
                        {isIn ? <ArrowDownLeft size={16} /> : <Table2 size={16} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold capitalize m-0">{txnLabel(txn.type)}</p>
                        <p className="text-xs text-cm-muted m-0 truncate">
                          {txn.club} · {new Date(txn.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <span className={cx('text-sm font-bold tabular-nums', isIn ? 'text-cm-success' : 'text-cm-text')}>
                        {isIn ? '+' : '−'}{formatINR(txn.amount)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <EmptyState compact icon={Receipt} title="No activity yet" description="Top-ups, buy-ins and cash-outs will show up here." />
            )}
          </div>
        </section>

        {/* How to play */}
        <section className="mt-6">
          <h2 className="font-sans text-base font-bold mb-2.5">How to play</h2>
          <div className="flex flex-col gap-2">
            {tutorials && tutorials.length > 0 ? (
              tutorials.map((tutorial) => {
                const open = expandedTutorial === tutorial.id
                return (
                  <div key={tutorial.id} className="glass-dark overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedTutorial(open ? null : tutorial.id)}
                      aria-expanded={open}
                      className="w-full flex items-center gap-3 p-3 text-left"
                    >
                      <span className="w-11 h-11 rounded-cm grid place-items-center bg-cm-red-soft text-cm-red shrink-0">
                        <PlayCircle size={20} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold">{tutorial.title}</span>
                        <span className="block text-xs text-cm-muted truncate">{tutorial.description}</span>
                      </span>
                      <StatusPill size="sm" tone={tutorial.type === 'beginner' ? 'info' : 'gold'} icon={null}>
                        {tutorial.type === 'beginner' ? 'Beginner' : 'Advanced'}
                      </StatusPill>
                      <ChevronDown size={16} className={cx('text-cm-muted transition-transform', open && 'rotate-180')} />
                    </button>
                    {open && (
                      <ul className="m-0 px-4 pb-4 pt-1 list-none flex flex-col gap-2 border-t border-cm-border">
                        {tutorial.content?.map((item, idx) => (
                          <li key={idx} className="flex gap-2 text-[13px] text-cm-text leading-relaxed pt-2">
                            <span className="text-cm-gold-text">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })
            ) : (
              <Skeleton className="h-16" />
            )}
          </div>
        </section>
      </PlayerPage>
  )
}

export default Dashboard
